/**
 * Hook personalizado para criação de novos recursos.
 *
 * Este hook gerencia o estado e a lógica necessários para criar um novo recurso,
 * incluindo validação de formulário, comunicação com a API e feedback ao usuário.
 *
 * @module useNewResource
 */

import { EditorState, convertToRaw } from 'draft-js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

/**
 * Hook que gerencia o fluxo de criação de um novo recurso.
 *
 * @returns {Object} Objeto contendo estados e funções para criação de recursos
 * @returns {string|number} returns.category - ID da categoria selecionada
 * @returns {Function} returns.setCategory - Função para atualizar a categoria
 * @returns {string} returns.type - Tipo do recurso
 * @returns {Function} returns.setType - Função para atualizar o tipo
 * @returns {string} returns.name - Nome/título do recurso
 * @returns {Function} returns.setName - Função para atualizar o nome
 * @returns {EditorState} returns.editorContent - Estado do editor de texto
 * @returns {Function} returns.setEditorContent - Função para atualizar o estado do editor
 * @returns {Function} returns.createResource - Função para submeter a criação do recurso
 * @returns {string} returns.errorMsg - Mensagem de erro
 * @returns {string} returns.successMsg - Mensagem de sucesso
 * @returns {Object} returns.errors - Objeto com erros de validação
 */
export const useNewResource = () => {
  // Hooks para tradução e navegação
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Estados para gerenciar o formulário e o processo de criação
  const [editorContent, setEditorContent] = useState(() =>
    EditorState.createEmpty()
  );
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [name, setName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [errors, setErrors] = useState({});

  /**
   * Verifica se o conteúdo do editor está vazio.
   *
   * @function isEditorContentEmpty
   * @param {EditorState} editorState - Estado atual do editor
   * @returns {boolean} True se o editor estiver vazio, false caso contrário
   */
  const isEditorContentEmpty = (editorState) => {
    const contentState = editorState.getCurrentContent();
    const plainText = contentState.getPlainText().trim();
    return plainText === '';
  };

  /**
   * Valida os campos do formulário antes de submeter.
   *
   * @function validateFormInputs
   * @returns {Object} Objeto contendo o resultado da validação
   * @returns {boolean} returns.isValid - Indica se o formulário é válido
   * @returns {string} [returns.message] - Mensagem de erro (apenas quando inválido)
   */
  const validateFormInputs = () => {
    const newErrors = {};

    if (!category) newErrors.category = true;
    if (!type.trim()) newErrors.type = true;
    if (!name.trim()) newErrors.name = true;
    if (isEditorContentEmpty(editorContent)) newErrors.editor = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return {
        isValid: false,
        message: t('Please fill in all required fields.'),
      };
    }

    setErrors({});
    return { isValid: true };
  };

  /**
   * Limpa todos os campos do formulário.
   *
   * @function clearForm
   */
  const clearForm = () => {
    setCategory('');
    setType('');
    setName('');
    setEditorContent(EditorState.createEmpty());
  };

  /**
   * Submete o formulário para criar um novo recurso na API.
   *
   * @async
   * @function createResource
   * @param {React.FormEvent} e - Evento de submit do formulário
   */
  const createResource = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    // Valida os campos do formulário
    const validation = validateFormInputs();

    if (!validation.isValid) {
      setErrorMsg(validation.message);
      return;
    }

    // Prepara os dados para envio à API
    const requestData = {
      category_id: parseInt(category),
      type,
      title: name,
      description: JSON.stringify(
        convertToRaw(editorContent.getCurrentContent())
      ),
    };

    try {
      // Envia os dados para a API
      await api.post('create-resources', requestData);

      // Limpa o formulário e exibe mensagem de sucesso
      clearForm();
      setSuccessMsg('Cadastro de recurso realizado com sucesso!');

      // Redireciona para a página de recursos após 2 segundos
      setTimeout(() => {
        navigate('/resources');
      }, 2000);
    } catch (error) {
      setErrorMsg('Error registering');
      console.error('Erro:', error);
    }
  };

  return {
    category,
    setCategory,
    type,
    setType,
    name,
    setName,
    editorContent,
    setEditorContent,
    createResource,
    errorMsg,
    successMsg,
    errors,
  };
};
